import {AfterViewInit, Component, Input} from '@angular/core';
import * as $ from "jquery";
import * as d3 from "d3";
import moment from "moment/moment";
import {HoursAndMinutesPipe} from '../../../../shared/pipes/hours-and-minutes.pipe';
import {CommonService} from '../../../../shared/services/common.service';
import {DatePipe} from '@angular/common';

@Component({
  selector: 'app-screen-time-chart',
  templateUrl: './screen-time-chart.component.html',
  styleUrls: ['./screen-time-chart.component.css']
})
export class ScreenTimeChartComponent implements AfterViewInit {
  @Input() data;

  chartData: any[] = [];

  colorMap = {
    stPoints: '#F9D949',
    targetNotMet: '#0070E0',
    imputed: '#A7E6FF',
    target: '#3C486B'
  }

  legend = [
    {name: 'Red Line', color: '#F9D949'},
    {name: 'Target', color: '#3C486B'},

  ]

  svg: any;
  margins = {
    top: 10,
    right: 0,
    bottom: 50,
    left: 20,
  };
  width = 100;
  height = 100;

  isWarned = false;

  resizeObserver: ResizeObserver;

  constructor(private hoursAndMinutesPipe: HoursAndMinutesPipe, private commonService: CommonService, private datePipe: DatePipe) {
    // creating resize observer for detecting size change of chart container
    this.resizeObserver = new ResizeObserver(() => {
      this.ngAfterViewInit();
    })
  }


  ngOnChanges() {
    this.ngAfterViewInit();
  }

  ngOnInit() {
    setTimeout(() => {
      const chartContainer = document.getElementById('screen-time-chart');
      this.resizeObserver.observe(chartContainer);
    });
  }


  ngAfterViewInit() {
    if (this.data) {
      this.chartData = JSON.parse(JSON.stringify(this.data));
      this.chartData.forEach(o => {
        o.stPoints = o.stPoints / 60;
      })
      const container = $('#screen-time-chart');
      this.width = container.width();
      this.height = container.height();
      this.createChart();
    }
  }

  createChart() {
    // Kept this warning for the QA and any other user who might face this
    // issue.
    if (!this.isWarned) {
      console.warn(`The chart disappears when the screen is in responsive and the screen size selection is changed from 'Responsive' to any other mobile screen and vice-versa. This is a known issue and needs to be explored. However, this issue does not impact the application or the user experience.`);
    }

    this.isWarned = true;

    d3.select("#screen-time-chart svg").remove();
    this.svg = d3.select("#screen-time-chart")
      .append("svg")
      .attr("width", '100%')
      .attr("height", '100%')
      .attr('viewBox', `0, -10, ${this.width}, ${this.height + this.margins.bottom - 15}`)
      .attr('preserveAspectRatio', 'xMidYMid meet')
      .append("g")

    // Create the X-axis band scale
    const x = d3.scaleBand()
      .range([0, this.width])
      .domain(this.chartData?.map(d => this.datePipe.transform(d.date, 'MMMdd')))
      .padding(0.5)

    // Create the Y-axis band scale

    const yMax = d3.max(this.chartData, (d) => Math.max(d['stPoints'])) || 0
    const y = d3.scaleLinear()
      .domain([0, yMax])
      .rangeRound([this.height, 0]);

    const yAxis = d3.axisLeft(y);
    this.svg.append('g')
      .call((() => {
          if (yMax === 0) {
            return yAxis.tickValues([])
          } else {
            return yAxis
              .ticks((() => {
                if (yMax < 4) {
                  return yMax;
                }

                return 4;
              })())
              .tickFormat((d) => d3.format(',')(Math.floor(Number(d))))
          }
        })()
      )

    // ********** Create tooltip ********** //
    const tooltip = d3.select("#screen-time-chart")
      .append("div")
      .attr('class', 'tooltip')
      .style("position", "fixed")
      .style("visibility", 'hidden')

    // Create and fill the bars
    const selector = this.svg.selectAll(".bar")
      .data(this.chartData)
      .enter()

    selector.append("rect")
      .attr("x", (d: any) => x(this.datePipe.transform(d.date, 'MMMdd')))
      .attr("y", (d: any) => y(d.stPoints))
      .attr("width", x.bandwidth())
      .attr("height", (d: any) => d.stPoints ? (this.height - y(d.stPoints)) : 0)
      .attr('fill', (d: any) => {
        if (d.isStImputed) {
          return this.colorMap.imputed;
        }
        return d.notMeetingTarget ? this.colorMap.stPoints : this.colorMap.targetNotMet
      })
      .on("mouseover", (event: any, d) => {
        return tooltip
          .html(`
            ${d.isStImputed ? `Estimated<br>` : ``}
            ${moment(d.date).format('MMM DD, YYYY')}<br>
            ${this.commonService.convertMinutesToHrsAndMinutes(d.stPoints * 60)} Screen Time
          `)
          .transition()
          .duration(200)
          .style('top', (event.clientY - (d.isStImputed ? this.commonService.convertRemToPx(9) : this.commonService.convertRemToPx(7))) + 'px')
          .style("left", this.commonService.calculateXForPopup(tooltip, event.clientX) + "px")
          .style("visibility", 'visible');

      })
      .on("mousemove", (event: any, d) => {
        return tooltip
          .transition()
          .duration(0)
          .style('top', (event.clientY - (d.isStImputed ? this.commonService.convertRemToPx(9) : this.commonService.convertRemToPx(7))) + 'px')
          .style("left", this.commonService.calculateXForPopup(tooltip, event.clientX) + "px");
      })
      .on("mouseout", (event: any) => {
        return tooltip
          .transition()
          .duration(0)
          .style("visibility", 'hidden');
      });

    // Draw the X-axis on the DOM
    this.svg.append("g")
      .attr("transform", "translate(0," + this.height + ")")
      .call(d3.axisBottom(x))
      .selectAll("text")
      .style("text-anchor", "center")
      .style("font-size", "1.2rem")
      .call(function (t) {
        t.each(function () { // for each one
          const self = d3.select(this);
          const s = self.text().split(' ');  // get the text and split it
          self.text(''); // clear it out
          s.forEach((d, i) => {
            self.append("tspan")
              .attr("x", 0)
              .attr("dy", "1em")
              .text(s[i]);
          })
        })
      })
  }

  ngOnDestroy() {
    this.resizeObserver.disconnect();
  }
}
